import type {
  MsgChildToParent,
  MsgParentToChild,
  PayloadStatus,
  PayloadError,
  PayloadParentToChild,
  BridgeParentEventType,
  BridgeMeta,
} from "../shared/types"
import { createId } from "../shared/ids"
import { isAllowedOrigin, isEnvelope, isEventType } from "../shared/validate"
import { BRIDGE_EVENTS, BRIDGE_PROTOCOL } from "../shared/constants"

export type ParentBridgeOptions = {
  allowedChildOrigins: string[] // e.g. ["https://ai.partner.com"]
  childWindowGetter: () => Window | null // iframe.contentWindow
  metaGetter?: () => BridgeMeta // iframe.contentWindow
  childTargetOrigin: string // exact origin (no "*")
  onReady?: () => void
  onError?: (error: PayloadError) => void
  onDisconnect?: () => void
  onEvent?: (msg: MsgChildToParent, meta: BridgeMeta) => Promise<void> | void
  // Fires when a bridge:sync is received, before the SYNC_ACK reply is sent.
  // Lets a consumer distinguish "child never started the handshake" from
  // "handshake started but never finished" without adding a 3rd status.
  onSync?: () => void
  // Fires when an envelope that matches our protocol/version arrives from an
  // origin outside allowedChildOrigins — the most common misconfiguration
  // (iframeUrl/iframeOrigin mismatch). Gated on isEnvelope so unrelated
  // postMessage traffic (webpack-dev-server, browser extensions, devtools)
  // never reaches this callback.
  onRejectedOrigin?: (origin: string) => void
}

export const createParentBridge = (options: ParentBridgeOptions) => {
  const bridgeState = {
    ready: false,
  }

  const isReady = () => bridgeState.ready

  const postToChild = (msg: MsgParentToChild) => {
    const childWindow = options.childWindowGetter()
    if (!childWindow) return

    childWindow.postMessage(msg, options.childTargetOrigin)
  }

  const sendSyncAck = () => {
    const payload: PayloadStatus = { status: "ok" }
    postToChild({
      protocol: BRIDGE_PROTOCOL.CHANNEL,
      version: BRIDGE_PROTOCOL.VERSION,
      type: BRIDGE_EVENTS.SYNC_ACK,
      id: createId(),
      ts: Date.now(),
      meta: options?.metaGetter?.() ?? {},
      payload,
    })
  }

  const sendError = (type: PayloadError["type"] | string, message: string) => {
    postToChild({
      protocol: BRIDGE_PROTOCOL.CHANNEL,
      version: BRIDGE_PROTOCOL.VERSION,
      type: BRIDGE_EVENTS.ERROR,
      id: createId(),
      ts: Date.now(),
      meta: options?.metaGetter?.() ?? {},
      payload: { type, message },
    })
  }

  const sendCancelProcess = () => {
    postToChild({
      protocol: BRIDGE_PROTOCOL.CHANNEL,
      version: BRIDGE_PROTOCOL.VERSION,
      type: BRIDGE_EVENTS.APP_CANCEL_PROCESS,
      id: createId(),
      ts: Date.now(),
      meta: options?.metaGetter?.() ?? {},
      payload: {},
    })
  }

  const send = (type: BridgeParentEventType, payload: PayloadParentToChild) => {
    postToChild({
      protocol: BRIDGE_PROTOCOL.CHANNEL,
      version: BRIDGE_PROTOCOL.VERSION,
      type,
      id: createId(),
      ts: Date.now(),
      meta: options?.metaGetter?.() ?? {},
      payload,
    })
  }

  const onMessage = async (event: MessageEvent) => {
    // security: validate origin + reject unknown protocol/version/type
    if (!isAllowedOrigin(event.origin, options.allowedChildOrigins)) {
      // Still drop the message either way — this only adds a diagnostic
      // callback, it never changes what gets processed. Gated on isEnvelope
      // so unrelated postMessage noise from other origins isn't reported.
      if (isEnvelope(event.data)) {
        options?.onRejectedOrigin?.(event.origin)
      }
      return
    }
    if (!isEnvelope(event.data)) return
    if (!isEventType(event.data.type)) return

    const msg = event.data as unknown as MsgChildToParent
    // handshake flow
    if (msg.type === BRIDGE_EVENTS.SYNC) {
      options?.onSync?.()
      sendSyncAck()
      return
    }

    if (msg.type === BRIDGE_EVENTS.ACK) {
      bridgeState.ready = true
      options?.onReady?.()
      return
    }

    if (msg.type === BRIDGE_EVENTS.DISCONNECT) {
      bridgeState.ready = false
      options?.onDisconnect?.()
      return
    }

    if (!isReady()) {
      const message = "bridge not ready (missing bridge:ack)"
      sendError(msg.type, message)
      options?.onError?.({
        type: msg.type,
        message,
      })
      return
    }

    try {
      const meta = msg?.meta || {}
      await options.onEvent(msg, { origin: event.origin, ...meta })
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown error"
      sendError(msg.type, message)
      options?.onError?.({
        type: msg.type,
        message,
      })
    }
  }

  const attach = () => window.addEventListener("message", onMessage)
  const detach = () => window.removeEventListener("message", onMessage)

  return { attach, detach, isReady, send, sendCancelProcess }
}
